if (!customElements.get('seed-frequently-bought-together')) {
  customElements.define(
    'seed-frequently-bought-together',
    class SeedFrequentlyBoughtTogether extends HTMLElement {
      constructor() {
        super();

        this.currencyCode = this.dataset.currencyCode || 'USD';
        this.submitButton = this.querySelector('[data-seed-fbt-submit]');
        this.totalElement = this.querySelector('[data-seed-fbt-total]');
        this.compareElement = this.querySelector('[data-seed-fbt-compare]');
        this.currentInput = this.querySelector('[data-seed-fbt-current]');
        this.currentPriceElement = this.querySelector('[data-seed-fbt-current-price]');
        this.currentCompareElement = this.querySelector('[data-seed-fbt-current-compare]');
        this.currentDiscountElement = this.querySelector('[data-seed-fbt-current-discount]');
        this.productInfo = this.closest('product-info');
        this.discount = { code: '', percent: 0 };
        this.handleQuantityChange = () => this.updateTotal();
        this.handleKlipDiscount = (event) => {
          this.setDiscount(event.detail);
          this.applyCurrentDiscount();
        };
      }

      connectedCallback() {
        this.cacheCurrentOriginalPrices();
        this.setDiscount({
          code: this.productInfo?.dataset.seedKlipDiscountCode,
          percent: this.productInfo?.dataset.seedKlipDiscountPercent,
        });

        this.addEventListener('change', (event) => {
          if (event.target.matches('[data-seed-fbt-variant-select]')) {
            this.onBundleVariantSelect(event);
            return;
          }

          if (!event.target.matches('.seed-fbt__checkbox')) return;
          this.updateTotal();
        });

        this.addEventListener('click', (event) => {
          if (!event.target.matches('[data-seed-fbt-variant-select]')) return;
          event.stopPropagation();
        });

        this.submitButton?.addEventListener('click', () => this.addSelectedItems());
        this.quantityInput = document.querySelector(`#Quantity-${this.dataset.sectionId}`);
        this.quantityInput?.addEventListener('change', this.handleQuantityChange);
        this.productInfo?.addEventListener('seed:klip-discount', this.handleKlipDiscount);
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => this.onVariantChange(data));
        this.updateVariantSelectWidths();
        this.applyCurrentDiscount();
      }

      onBundleVariantSelect(event) {
        const select = event.target;
        const item = select.closest('.seed-fbt__item');
        const input = item?.querySelector('.seed-fbt__checkbox');
        const option = select.selectedOptions?.[0];

        if (!item || !input || !option) return;

        this.updateVariantSelectWidth(select);
        input.dataset.variantId = option.value;

        if (input.hasAttribute('data-seed-fbt-current')) {
          input.dataset.originalPriceCents = option.dataset.priceCents || '0';
          input.dataset.originalComparePriceCents = option.dataset.comparePriceCents || option.dataset.priceCents || '0';
          input.dataset.variantAvailable = option.dataset.variantAvailable || 'true';
          this.updateItemImage(item, option);
          this.updateItemAvailability(item, input);
          this.applyCurrentDiscount();
          return;
        }

        input.dataset.priceCents = option.dataset.priceCents || '0';
        input.dataset.comparePriceCents = option.dataset.comparePriceCents || option.dataset.priceCents || '0';
        input.dataset.variantAvailable = option.dataset.variantAvailable || 'true';
        this.updateItemPrice(item, input);
        this.updateItemImage(item, option);
        this.updateItemAvailability(item, input);
        this.updateTotal();
      }

      updateVariantSelectWidths() {
        this.querySelectorAll('[data-seed-fbt-variant-select]').forEach((select) => this.updateVariantSelectWidth(select));
      }

      updateVariantSelectWidth(select) {
        const longestOption = Array.from(select.options).reduce((longest, option) => {
          const text = option.textContent.trim();
          return text.length > longest.length ? text : longest;
        }, '');
        const width = Math.max(5.8, Math.min(longestOption.length + 3, 14));

        select.style.width = `${width}ch`;
      }

      disconnectedCallback() {
        this.quantityInput?.removeEventListener('change', this.handleQuantityChange);
        this.productInfo?.removeEventListener('seed:klip-discount', this.handleKlipDiscount);
        this.variantChangeUnsubscriber?.();
      }

      onVariantChange({ sectionId, variant }) {
        if (!variant || sectionId !== this.dataset.sectionId || !this.currentInput) return;

        this.currentInput.dataset.variantId = variant.id;
        this.currentInput.dataset.originalPriceCents = variant.price;
        this.currentInput.dataset.originalComparePriceCents = variant.compare_at_price || variant.price;
        this.currentInput.dataset.variantAvailable = variant.available ? 'true' : 'false';

        const variantTitle = this.querySelector('[data-seed-fbt-current-variant]');

        if (variantTitle) variantTitle.textContent = variant.public_title || variant.title || '';

        const currentSelect = this.querySelector('[data-seed-fbt-current-select]');
        const matchingOption = currentSelect?.querySelector(`option[value="${variant.id}"]`);
        if (currentSelect && matchingOption) {
          currentSelect.value = String(variant.id);
          this.updateItemImage(currentSelect.closest('.seed-fbt__item'), matchingOption);
        }

        this.updateItemAvailability(this.currentInput.closest('.seed-fbt__item'), this.currentInput);
        this.applyCurrentDiscount();
      }

      getSelectedInputs() {
        return Array.from(this.querySelectorAll('.seed-fbt__checkbox:checked')).filter(
          (input) => input.dataset.variantId && input.dataset.variantAvailable !== 'false'
        );
      }

      getSelectedOfferInputs() {
        return this.getSelectedInputs().filter((input) => !input.hasAttribute('data-seed-fbt-current'));
      }

      cacheCurrentOriginalPrices() {
        if (!this.currentInput) return;

        this.currentInput.dataset.originalPriceCents =
          this.currentInput.dataset.originalPriceCents || this.currentInput.dataset.priceCents || '0';
        this.currentInput.dataset.originalComparePriceCents =
          this.currentInput.dataset.originalComparePriceCents ||
          this.currentInput.dataset.comparePriceCents ||
          this.currentInput.dataset.originalPriceCents;
      }

      setDiscount(discount = {}) {
        const percent = Number.parseFloat(discount.percent || 0);
        const code = String(discount.code || '').trim();

        this.discount = {
          code,
          percent: code && Number.isFinite(percent) && percent > 0 && percent < 100 ? percent : 0,
        };
      }

      applyCurrentDiscount() {
        if (!this.currentInput) {
          this.updateTotal();
          return;
        }

        this.cacheCurrentOriginalPrices();

        const originalPrice = Number(this.currentInput.dataset.originalPriceCents || 0);
        const originalCompare = Number(this.currentInput.dataset.originalComparePriceCents || originalPrice);
        const discountedPrice = this.getDiscountedPrice(originalPrice);
        const hasCouponDiscount = this.discount.percent > 0 && discountedPrice < originalPrice;
        const effectiveCompare = hasCouponDiscount ? Math.max(originalCompare, originalPrice) : originalCompare;

        this.currentInput.dataset.priceCents = String(hasCouponDiscount ? discountedPrice : originalPrice);
        this.currentInput.dataset.comparePriceCents = String(effectiveCompare);

        if (this.currentPriceElement) {
          this.currentPriceElement.textContent = this.formatMoney(hasCouponDiscount ? discountedPrice : originalPrice);
        }

        if (this.currentCompareElement) {
          const showCompare = hasCouponDiscount || effectiveCompare > originalPrice;
          this.currentCompareElement.textContent = showCompare ? this.formatMoney(effectiveCompare) : '';
          this.currentCompareElement.hidden = !showCompare;
        }

        if (this.currentDiscountElement) {
          const unavailable = this.currentInput.dataset.variantAvailable === 'false';
          this.currentDiscountElement.textContent = unavailable
            ? 'Sold out'
            : hasCouponDiscount
              ? `Save ${this.formatPercent(this.discount.percent)}`
              : '';
          this.currentDiscountElement.hidden = !unavailable && !hasCouponDiscount;
          this.currentDiscountElement.classList.toggle('is-sold-out', unavailable);
        }

        this.updateTotal();
      }

      updateTotal() {
        const inputs = this.getSelectedInputs();
        const total = inputs.reduce((sum, input) => sum + Number(input.dataset.priceCents || 0) * this.getInputQuantity(input), 0);
        const compareTotal = inputs.reduce(
          (sum, input) =>
            sum + Number(input.dataset.comparePriceCents || input.dataset.priceCents || 0) * this.getInputQuantity(input),
          0
        );

        if (this.totalElement) this.totalElement.textContent = this.formatMoney(total);
        if (this.compareElement) {
          this.compareElement.textContent = compareTotal > total ? this.formatMoney(compareTotal) : '';
          this.compareElement.hidden = !(compareTotal > total);
        }

        this.updateSubmitState();
      }

      updateSubmitState() {
        if (!this.submitButton) return;

        const hasUnavailableSelected = Array.from(this.querySelectorAll('.seed-fbt__checkbox:checked')).some(
          (input) => input.dataset.variantAvailable === 'false'
        );

        this.submitButton.disabled = hasUnavailableSelected || this.getSelectedOfferInputs().length === 0;
      }

      updateItemPrice(item, input) {
        const priceElement = item?.querySelector('[data-seed-fbt-item-price]');
        const compareElement = item?.querySelector('[data-seed-fbt-item-compare]');
        const price = Number(input?.dataset.priceCents || 0);
        const comparePrice = Number(input?.dataset.comparePriceCents || price);

        if (priceElement) priceElement.textContent = this.formatMoney(price);

        if (compareElement) {
          compareElement.textContent = comparePrice > price ? this.formatMoney(comparePrice) : '';
          compareElement.hidden = !(comparePrice > price);
        }
      }

      updateItemImage(item, option) {
        const image = item?.querySelector('[data-seed-fbt-image]');
        const imageSrc = option?.dataset.imageSrc;

        if (!image || !imageSrc) return;

        image.src = imageSrc;
        image.removeAttribute('srcset');
        if (option.dataset.imageAlt) image.alt = option.dataset.imageAlt;
      }

      updateItemAvailability(item, input) {
        if (!item || !input) return;

        const unavailable = input.dataset.variantAvailable === 'false';
        const checkbox = item.querySelector('.seed-fbt__checkbox');

        item.classList.toggle('is-sold-out', unavailable);

        if (!input.hasAttribute('data-seed-fbt-current')) {
          checkbox.disabled = unavailable;
          if (unavailable) checkbox.checked = false;
        }
      }

      addSelectedItems() {
        const inputs = this.getSelectedInputs();
        if (!inputs.length || !this.submitButton || this.getSelectedOfferInputs().length === 0 || this.submitButton.disabled) return;

        this.submitButton.disabled = true;
        this.submitButton.classList.add('is-loading');

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const discountCode = this.getDiscountCode();
        const body = {
          items: inputs.map((input) => ({
            id: Number(input.dataset.variantId),
            quantity: this.getInputQuantity(input),
          })),
        };

        if (cart && !discountCode) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement?.(this.submitButton);
        }

        const config = fetchConfig('javascript');
        config.body = JSON.stringify(body);

        fetch(this.dataset.cartAddUrl || routes.cart_add_url, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) throw new Error(response.description || response.message);

            const firstItem = response.items?.[0];
            if (firstItem) {
              response.key = firstItem.key;
              response.id = firstItem.id;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'seed-frequently-bought-together',
              cartData: response,
            });

            if (discountCode) {
              window.location.href = this.getDiscountUrl(discountCode);
              return;
            }

            if (cart && response.sections) {
              cart.renderContents(response);
              return;
            }

            window.location = this.dataset.cartUrl || routes.cart_url;
          })
          .catch((error) => {
            console.error(error);
          })
          .finally(() => {
            this.submitButton.classList.remove('is-loading');
            this.updateSubmitState();
          });
      }

      getDiscountedPrice(cents) {
        const price = Number(cents || 0);
        if (!this.discount.percent || price <= 0) return price;

        const discountCents = Math.floor((price * this.discount.percent) / 100);
        return Math.max(price - discountCents, 0);
      }

      getDiscountCode() {
        return this.discount.percent > 0 ? this.discount.code : '';
      }

      getDiscountUrl(code) {
        const rootUrl = this.dataset.rootUrl || window.Shopify?.routes?.root || '/';
        const normalizedRoot = rootUrl.endsWith('/') ? rootUrl : `${rootUrl}/`;
        const cartUrl = this.dataset.cartUrl || routes.cart_url || `${normalizedRoot}cart`;

        return `${normalizedRoot}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(cartUrl)}`;
      }

      formatPercent(percent) {
        const number = Number.parseFloat(percent);
        if (!Number.isFinite(number)) return '';

        return `${Number.isInteger(number) ? number : number.toFixed(1)}%`;
      }

      formatMoney(cents) {
        const amount = Number(cents || 0) / 100;

        try {
          return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
            style: 'currency',
            currency: this.currencyCode,
            currencyDisplay: 'narrowSymbol',
          }).format(amount);
        } catch (error) {
          return `$${amount.toFixed(2)}`;
        }
      }

      getInputQuantity(input) {
        if (!input.hasAttribute('data-seed-fbt-current')) return 1;

        const quantityInput = document.querySelector(`#Quantity-${this.dataset.sectionId}`);
        const quantity = Number.parseInt(quantityInput?.value, 10);
        return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
      }
    }
  );
}
